import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';
import {
  fetchOnboardingStatus,
  postOnboardingStart,
  postOnboardingComplete,
  postOnboardingReset,
  putOnboardingStep,
  type OnboardingLifecycleStatus,
} from '@/services/onboardingService';
import { getAuthConfig } from '@/lib/auth-fetch';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OnboardingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface StepStatus {
  stepIndex: number; // 1-5
  completed: boolean;
  skipped: boolean;
}

interface OnboardingContextValue {
  // State
  hasDismissed: boolean;
  /**
   * True once the wizard has been fully completed.
   * When true the onboarding button is disabled in the sidebar and the
   * wizard will never auto-show again (until a new company is created).
   */
  hasCompleted: boolean;
  /**
   * True once any admin has opened the wizard (but not yet completed it).
   * Other admins will see a "Resume Onboarding" option in the sidebar.
   */
  isInProgress: boolean;
  isWizardOpen: boolean;
  /** True when the wizard was temporarily closed to allow bulk work elsewhere in the app */
  isPausedForBulk: boolean;
  savedStep: OnboardingStep;
  stepStatuses: StepStatus[];
  /** True while the initial server-side status check is in flight */
  isCheckingServer: boolean;
  /**
   * The authoritative server-side lifecycle status for the current company.
   * 'notStarted' → no admin has opened the wizard yet (auto-show to first admin)
   * 'inProgress'  → started but not finished (other admins see Resume option)
   * 'completed'   → done; no more showing
   */
  serverStatus: OnboardingLifecycleStatus;

  // Actions
  openWizard: () => void;
  closeWizard: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  saveStep: (step: OnboardingStep) => void;
  markStepCompleted: (stepIndex: number) => void;
  markStepSkipped: (stepIndex: number) => void;
  /**
   * Temporarily close the wizard so the admin can do bulk work elsewhere.
   * Does NOT dismiss — the wizard re-opens automatically when the admin returns.
   */
  pauseForBulk: () => void;
  /** Resume the wizard after a bulk-work detour. */
  resumeFromBulk: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DEFAULT_STEP_STATUSES: StepStatus[] = [
  { stepIndex: 1, completed: false, skipped: false },
  { stepIndex: 2, completed: false, skipped: false },
  { stepIndex: 3, completed: false, skipped: false },
  { stepIndex: 4, completed: false, skipped: false },
  { stepIndex: 5, completed: false, skipped: false },
];

/**
 * Returns the instanceId that identifies this workspace on the backend.
 * Falls back to "default-instance" when not running in production mode.
 */
function getInstanceId(): string {
  return getAuthConfig().instanceId ?? 'default-instance';
}

/**
 * Build a company-scoped localStorage key so that switching company resets
 * the local cache automatically.
 */
function scopedKey(base: string): string {
  const instanceId = getInstanceId();
  return `${base}_${instanceId}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  // ── Persisted via useLocalStorage (fast cache + offline fallback) ──────────
  // Keys are instance-scoped so each company gets independent state.
  const instanceId = getInstanceId();

  const [hasCompleted, setHasCompleted] = useLocalStorage<boolean>(
    `onboarding_completed_${instanceId}`,
    false,
  );
  const [isInProgress, setIsInProgress] = useLocalStorage<boolean>(
    `onboarding_inprogress_${instanceId}`,
    false,
  );
  const [savedStep, setSavedStep] = useLocalStorage<OnboardingStep>(
    `onboarding_saved_step_${instanceId}`,
    0,
  );
  const [stepStatuses, setStepStatuses] = useLocalStorage<StepStatus[]>(
    `onboarding_step_statuses_${instanceId}`,
    DEFAULT_STEP_STATUSES,
  );

  // ── React-only state (resets on refresh) ──────────────────────────────────
  const [hasDismissed, setHasDismissed] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isPausedForBulk, setIsPausedForBulk] = useState(false);
  const [isCheckingServer, setIsCheckingServer] = useState(true);
  const [serverStatus, setServerStatus] = useState<OnboardingLifecycleStatus>('notStarted');

  // Track which instanceId we last fetched so that a company switch triggers
  // a fresh server check even if the component doesn't remount.
  const lastCheckedInstanceRef = useRef<string | null>(null);

  // ── Server status check ───────────────────────────────────────────────────
  const checkServer = useCallback(async () => {
    const currentInstanceId = getInstanceId();
    setIsCheckingServer(true);

    const status = await fetchOnboardingStatus(currentInstanceId);

    if (status !== null) {
      // Server is authoritative — sync local cache.
      const lifecycle = status.status;
      setServerStatus(lifecycle);

      const completed = lifecycle === 'completed';
      const inProgress = lifecycle === 'inProgress';

      setHasCompleted(completed);
      setIsInProgress(inProgress);

      // Restore step progress from server when in-progress
      if (inProgress && status.savedStep > 0) {
        setSavedStep(status.savedStep as OnboardingStep);
      }

      // Reset wizard step/statuses when server says notStarted
      // (e.g. after a new company is provisioned and reset was triggered)
      if (lifecycle === 'notStarted') {
        setSavedStep(0);
        setStepStatuses(DEFAULT_STEP_STATUSES);
      }
    } else {
      // Backend unreachable — derive server status from localStorage cache.
      const lifecycle: OnboardingLifecycleStatus = hasCompleted
        ? 'completed'
        : isInProgress
          ? 'inProgress'
          : 'notStarted';
      setServerStatus(lifecycle);
    }

    lastCheckedInstanceRef.current = currentInstanceId;
    setIsCheckingServer(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Run once on mount.
  useEffect(() => {
    checkServer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────

  const openWizard = useCallback(async () => {
    setIsWizardOpen(true);

    // If this is the first time any admin has opened the wizard for this
    // company/instance, mark it as inProgress on the server so other admins
    // see the "Resume Onboarding" option instead of the auto-show.
    if (serverStatus === 'notStarted') {
      const currentInstanceId = getInstanceId();
      setServerStatus('inProgress');
      setIsInProgress(true);
      // Fire-and-forget — optimistic update already applied above.
      postOnboardingStart(currentInstanceId).then((result) => {
        if (result) {
          setServerStatus(result.status);
          setIsInProgress(result.status === 'inProgress');
        }
      });
    }
  }, [serverStatus, setIsInProgress]);

  const closeWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  const dismissOnboarding = useCallback(() => {
    setHasDismissed(true);
    setIsPausedForBulk(false);
    setIsWizardOpen(false);
  }, []);

  /**
   * Temporarily hide the wizard so the admin can do bulk work on another page.
   * The wizard is NOT dismissed — isPausedForBulk lets the app show a "Return
   * to Onboarding" banner and reopen the wizard when the admin is done.
   */
  const pauseForBulk = useCallback(() => {
    setIsPausedForBulk(true);
    setIsWizardOpen(false);
  }, []);

  /** Re-open the wizard and clear the paused-for-bulk flag. */
  const resumeFromBulk = useCallback(() => {
    setIsPausedForBulk(false);
    setIsWizardOpen(true);
  }, []);

  /**
   * Mark onboarding as complete.
   * Writes to localStorage immediately (optimistic), then syncs to server.
   */
  const completeOnboarding = useCallback(async () => {
    setHasCompleted(true);
    setIsInProgress(false);
    setServerStatus('completed');
    setIsWizardOpen(false);

    // Fire-and-forget — if the server is down, localStorage already has the flag.
    const currentInstanceId = getInstanceId();
    await postOnboardingComplete(currentInstanceId);
  }, [setHasCompleted, setIsInProgress]);

  /**
   * Reset all onboarding state — both server and localStorage.
   * Used during development / admin override only.
   */
  const resetOnboarding = useCallback(async () => {
    // Clear local state immediately
    setHasCompleted(false);
    setIsInProgress(false);
    setSavedStep(0);
    setStepStatuses(DEFAULT_STEP_STATUSES);
    setHasDismissed(false);
    setIsPausedForBulk(false);
    setServerStatus('notStarted');
    setIsWizardOpen(false);

    // Sync reset to server (best-effort)
    const currentInstanceId = getInstanceId();
    await postOnboardingReset(currentInstanceId);
  }, [setHasCompleted, setIsInProgress, setSavedStep, setStepStatuses]);

  const saveStep = useCallback(
    (step: OnboardingStep) => {
      setSavedStep(step);
      // Persist step progress to server (fire-and-forget)
      putOnboardingStep(step, 0);
    },
    [setSavedStep],
  );

  const markStepCompleted = useCallback(
    (stepIndex: number) => {
      setStepStatuses((prev) =>
        prev.map((s) =>
          s.stepIndex === stepIndex
            ? { ...s, completed: true, skipped: false }
            : s,
        ),
      );
    },
    [setStepStatuses],
  );

  const markStepSkipped = useCallback(
    (stepIndex: number) => {
      setStepStatuses((prev) => {
        const updated = prev.map((s) =>
          s.stepIndex === stepIndex
            ? { ...s, skipped: true, completed: false }
            : s,
        );
        // Persist skip count to server (fire-and-forget)
        const skipCount = updated.filter((s) => s.skipped).length;
        putOnboardingStep(savedStep, skipCount);
        return updated;
      });
    },
    [setStepStatuses, savedStep],
  );

  return (
    <OnboardingContext.Provider
      value={{
        hasDismissed,
        hasCompleted,
        isInProgress,
        isWizardOpen,
        isPausedForBulk,
        savedStep,
        stepStatuses,
        isCheckingServer,
        serverStatus,
        openWizard,
        closeWizard,
        dismissOnboarding,
        completeOnboarding,
        resetOnboarding,
        saveStep,
        markStepCompleted,
        markStepSkipped,
        pauseForBulk,
        resumeFromBulk,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}
